// SVG icon for PWA — professional graduation cap on blue background
const PWA_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
  <defs>
    <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" style="stop-color:#1e40af"/>
      <stop offset="100%" style="stop-color:#7c3aed"/>
    </linearGradient>
  </defs>
  <rect width="512" height="512" rx="96" fill="url(#bg)"/>
  <g transform="translate(256,240)" fill="none" stroke="white" stroke-width="16" stroke-linecap="round" stroke-linejoin="round">
    <path d="M-120,20 L0,-40 L120,20 L0,80 Z" fill="white" opacity="0.95"/>
    <path d="M-80,45 L-80,100 Q0,140 80,100 L80,45" stroke="white" fill="white" opacity="0.3"/>
    <line x1="120" y1="20" x2="120" y2="100"/>
    <circle cx="120" cy="108" r="8" fill="white"/>
  </g>
  <text x="256" y="400" text-anchor="middle" font-family="Arial,sans-serif" font-size="48" font-weight="bold" fill="white" opacity="0.9">THEO EDU</text>
</svg>`

const ICON_DATA_URL = 'data:image/svg+xml,' + encodeURIComponent(PWA_ICON_SVG)

export function registerPWA() {
  const manifestData = {
    name: 'Theo EduMarket Rwanda',
    short_name: 'Theo EduMarket',
    description:
      'Digital Educational Marketplace for Rwanda — Buy past papers, books & study materials',
    start_url: '/',
    display: 'standalone',
    background_color: '#0f172a',
    theme_color: '#1e40af',
    orientation: 'portrait-primary',
    categories: ['education', 'books'],
    icons: [
      { src: ICON_DATA_URL, sizes: '192x192', type: 'image/svg+xml' },
      {
        src: ICON_DATA_URL,
        sizes: '512x512',
        type: 'image/svg+xml',
        purpose: 'any maskable',
      },
    ],
  }

  const blob = new Blob([JSON.stringify(manifestData)], {
    type: 'application/json',
  })
  const manifestUrl = URL.createObjectURL(blob)
  const link = document.createElement('link')
  link.rel = 'manifest'
  link.href = manifestUrl
  document.head.appendChild(link)

  // Theme color meta tag
  let metaTheme = document.querySelector('meta[name="theme-color"]')
  if (!metaTheme) {
    metaTheme = document.createElement('meta')
    metaTheme.setAttribute('name', 'theme-color')
    document.head.appendChild(metaTheme)
  }
  metaTheme.setAttribute('content', '#1e40af')

  // Apple touch icon
  const appleIcon = document.createElement('link')
  appleIcon.rel = 'apple-touch-icon'
  appleIcon.href = ICON_DATA_URL
  document.head.appendChild(appleIcon)

  // Apple mobile web app meta tags
  const appleMeta = document.createElement('meta')
  appleMeta.setAttribute('name', 'apple-mobile-web-app-capable')
  appleMeta.setAttribute('content', 'yes')
  document.head.appendChild(appleMeta)

  const appleStatus = document.createElement('meta')
  appleStatus.setAttribute('name', 'apple-mobile-web-app-status-bar-style')
  appleStatus.setAttribute('content', 'black-translucent')
  document.head.appendChild(appleStatus)

  // Register service worker
  if ('serviceWorker' in navigator) {
    const swCode = `
      const CACHE_NAME = 'theo-edumarket-v2';
      
      self.addEventListener('install', (e) => {
        self.skipWaiting();
      });
      
      self.addEventListener('activate', (e) => {
        e.waitUntil(
          caches.keys().then((names) => 
            Promise.all(names.filter(n => n !== CACHE_NAME).map(n => caches.delete(n)))
          ).then(() => clients.claim())
        );
      });
      
      self.addEventListener('fetch', (e) => {
        e.respondWith(
          caches.match(e.request).then((r) => r || fetch(e.request).catch(() => new Response('Offline')))
        );
      });
      
      self.addEventListener('push', (e) => {
        const data = e.data ? e.data.json() : { title: 'Theo EduMarket Rwanda', body: 'New notification' };
        e.waitUntil(
          self.registration.showNotification(data.title || 'Theo EduMarket Rwanda', {
            body: data.body || 'You have a new notification',
            icon: '${ICON_DATA_URL.replace(/'/g, "\\'")}',
            badge: '${ICON_DATA_URL.replace(/'/g, "\\'")}',
            vibrate: [200, 100, 200, 100, 200],
            tag: 'theo-edumarket-notification',
            renotify: true,
          })
        );
      });
      
      self.addEventListener('notificationclick', (e) => {
        e.notification.close();
        e.waitUntil(
          clients.matchAll({ type: 'window' }).then((clientList) => {
            for (const client of clientList) {
              if (client.url && 'focus' in client) return client.focus();
            }
            if (clients.openWindow) return clients.openWindow('/admin/dashboard');
          })
        );
      });
    `
    const swBlob = new Blob([swCode], { type: 'application/javascript' })
    const swUrl = URL.createObjectURL(swBlob)
    navigator.serviceWorker.register(swUrl).catch(() => {})
  }
}

// Notification permission
export async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) return false
  if (Notification.permission === 'granted') return true
  if (Notification.permission === 'denied') return false
  const result = await Notification.requestPermission()
  return result === 'granted'
}

export function showWebNotification(title: string, body: string) {
  if (!('Notification' in window) || Notification.permission !== 'granted')
    return
  try {
    new Notification(title, {
      body,
      icon: ICON_DATA_URL,
      badge: ICON_DATA_URL,
      vibrate: [200, 100, 200],
      tag: 'theo-edumarket-' + Date.now(),
    } as NotificationOptions)
  } catch {
    // Fallback for environments that don't support Notification constructor options
  }
}

// Install prompt handler
let deferredPrompt: any = null

export function setupInstallPrompt() {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferredPrompt = e
  })
}

export function canInstallPWA(): boolean {
  return !!deferredPrompt
}

export async function installPWA(): Promise<boolean> {
  if (!deferredPrompt) return false
  deferredPrompt.prompt()
  const result = await deferredPrompt.userChoice
  deferredPrompt = null
  return result.outcome === 'accepted'
}

export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
  )
}
